// Import React hooks
import { useState } from "react";

// Import utility functions
import { FormatCurrency } from "global/utilities/ConvertCurrency";

// Import css
import "./EarningBreakdown.css";



// Format a decimal affiliate rate as a percentage
const formatAffiliateRate = (rate, decimalPlaces = 2) => {

    // Convert the supplied decimal rate into a percentage
    const percentage = Number(rate || 0) * 100;

    // Return the formatted percentage without unnecessary trailing zeroes
    return percentage.toFixed(decimalPlaces).replace(/\.?0+$/, "") + "%";

};



const AffiliateEarningBreakdown = ({ affiliateEarning, transaction }) => {

    // Store the safe public source details
    const source = affiliateEarning?.source || {};

    // Store the safe public fulfilment product summaries
    const items = Array.isArray(affiliateEarning?.items) ? affiliateEarning.items : [];

    // Store the commission calculation
    const calculation = affiliateEarning?.calculation || {};

    // Store whether the commissionable value breakdown is open
    const [commissionableSectionIsOpen, setCommissionableSectionIsOpen] = useState(false);

    // Store any currency conversion applied when the earning reached the affiliate account
    const currencyConversion = transaction?.transaction_data?.currency_conversion || null;

    // Store the source, rate and final account currencies
    const sourceCurrency = calculation?.currency;
    const accountCurrency = transaction?.currency || sourceCurrency;

    // Stop when the source currency is unavailable
    if (!sourceCurrency) { return null; }

    // Use the stored calculation earning until a normalised transaction amount is available
    let finalAmount = calculation?.earning_amount || 0;
    if (transaction && transaction.amount !== null && transaction.amount !== undefined) { finalAmount = transaction.amount; }

    // Set the default explanation for the commissionable value
    let subtitle = "Commission is calculated from the commissionable value shown below.";

    // Explain fulfilment-based commission without exposing the referred account's product details
    if ([ "order", "marketplace_order", "fulfilment" ].includes(source?.type)) {
        subtitle = "Commission is calculated from fulfilment item charges only. Shipping and tax are excluded.";
    }

    // Explain subscription commission
    if (source?.type === "subscription") {
        subtitle = "Commission is calculated from the subscription price excluding tax.";
    }

    // Explain subscription upgrade commission
    if (source?.type === "subscription_upgrade") {
        subtitle = "Commission is calculated from the subscription upgrade charge excluding tax.";
    }

    // Set the commissionable value label for the source type
    let commissionableLabel = "Commissionable value";
    if ([ "order", "marketplace_order", "fulfilment" ].includes(source?.type)) { commissionableLabel = "Commissionable order value"; }
    if ([ "subscription", "subscription_upgrade" ].includes(source?.type)) { commissionableLabel = "Commissionable subscription value"; }

    // Render the affiliate earning breakdown
    return (

        <div className="earning-breakdown">

            {/* This is the summary header */}
            <div className="earning-breakdown__header">

                {/* This is the title */}
                <h3 className="earning-breakdown__title">Affiliate earning breakdown</h3>

                {/* This explains the commissionable value */}
                <p className="earning-breakdown__subtitle">
                    {subtitle}
                </p>

            </div>

            {/* This shows the commission calculation */}
            <div className="earning-breakdown__steps">

                {/* Show an expandable commissionable value breakdown when item details are available */}
                {items.length > 0

                    ?

                        <div className="earning-breakdown__section">

                            <button
                                type="button"
                                className="earning-breakdown__step earning-breakdown__step--breakdown earning-breakdown__step-button"
                                onClick={() => setCommissionableSectionIsOpen(!commissionableSectionIsOpen)}
                                aria-expanded={commissionableSectionIsOpen}
                            >

                                <span className="earning-breakdown__expandable-label">

                                    <span className="earning-breakdown__section-toggle" aria-hidden="true">
                                        {commissionableSectionIsOpen ? "−" : "+"}
                                    </span>

                                    <span className="earning-breakdown__label">{commissionableLabel}</span>

                                </span>

                                {/* Only show the commissionable total while collapsed */}
                                {!commissionableSectionIsOpen &&

                                    <strong className="earning-breakdown__value earning-breakdown__value--positive">
                                        {FormatCurrency(sourceCurrency, calculation?.commission_base || 0, false)}
                                    </strong>

                                }

                            </button>

                            {/* Show the contributing items while the section is open */}
                            {commissionableSectionIsOpen &&

                                <div className="earning-breakdown__items">

                                    {/* Loop through each public fulfilment product summary */}
                                    {items.map((item, index) => (

                                        <div key={`${item.product || "product"}-${index}`} className="earning-breakdown__item">

                                            <div className="earning-breakdown__item-main">

                                                <strong className="earning-breakdown__item-title">
                                                    {item.product || "Custom Product"}
                                                </strong>

                                                <span className="earning-breakdown__item-meta">
                                                    Qty {Number(item.quantity || 0)}
                                                </span>

                                            </div>

                                            <strong className="earning-breakdown__value">
                                                {FormatCurrency(sourceCurrency, item.commissionable || 0, false)}
                                            </strong>

                                        </div>

                                    ))}

                                    <div className="earning-breakdown__item earning-breakdown__item--section-total earning-breakdown__item--section-total-positive">

                                        <span className="earning-breakdown__item-title">{commissionableLabel} total</span>

                                        <strong className="earning-breakdown__value earning-breakdown__value--positive">
                                            {FormatCurrency(sourceCurrency, calculation?.commission_base || 0, false)}
                                        </strong>

                                    </div>

                                </div>

                            }

                        </div>

                    :

                        <div className="earning-breakdown__step">

                            <span className="earning-breakdown__label">{commissionableLabel}</span>

                            <strong className="earning-breakdown__value">
                                {FormatCurrency(sourceCurrency, calculation?.commission_base || 0, false)}
                            </strong>

                        </div>

                }

                {/* This shows the nominal affiliate rate */}
                <div className="earning-breakdown__step">

                    <span className="earning-breakdown__label">Nominal affiliate rate</span>

                    <strong className="earning-breakdown__value">
                        {formatAffiliateRate(calculation?.nominal_rate, 2)}
                    </strong>

                </div>

                {/* This shows the referral age factor */}
                <div className="earning-breakdown__step">

                    <span className="earning-breakdown__label">Referral age factor</span>

                    <strong className="earning-breakdown__value">
                        {formatAffiliateRate(calculation?.age_factor, 2)}
                    </strong>

                </div>

                {/* This shows the final applied affiliate rate */}
                <div className="earning-breakdown__step earning-breakdown__step--positive">

                    <span className="earning-breakdown__label">Applied affiliate rate</span>

                    <strong className="earning-breakdown__value">
                        {formatAffiliateRate(calculation?.applied_rate, 2)}
                    </strong>

                </div>

                {/* Show the converted earning when the source and account currencies differ */}
                {currencyConversion && accountCurrency !== sourceCurrency &&

                    <div className="earning-breakdown__step earning-breakdown__step--converted">

                        <span className="earning-breakdown__label">
                            Converted {FormatCurrency(sourceCurrency, currencyConversion?.original_amount || calculation?.earning_amount || 0, true)} to {accountCurrency} at exchange rate {currencyConversion?.exchange_rate}
                        </span>

                        <strong className="earning-breakdown__value">
                            {FormatCurrency(accountCurrency, transaction?.amount || 0, true)}
                        </strong>

                    </div>

                }

            </div>

            {/* This shows the final balance addition */}
            <div className="earning-breakdown__total">

                <span className="earning-breakdown__total-label">
                    Added to your balance
                </span>

                <strong className="earning-breakdown__total-value">
                    {FormatCurrency(accountCurrency, finalAmount, true)}
                </strong>

            </div>

        </div>

    );

};

export default AffiliateEarningBreakdown;